import {
  AlertOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CommentOutlined,
  ContainerOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  FormOutlined,
  HistoryOutlined,
  HomeOutlined,
  SettingOutlined,
  ShopOutlined,
  TeamOutlined,
  TruckOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { SharedSpinner } from '@components';
import { ComponentType, lazy, ReactNode, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { RequireAuth } from './RequireAuth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AppRoute {
  path?: string;
  index?: false;
  element?: ReactNode;
  permission?: string;
  title?: string;
  breadcrumb?: string;
  icon?: ReactNode;
  showInMenu?: boolean;
  children?: AppRoute[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const suspenseFallback = <SharedSpinner loading />;

/**
 * Wraps a lazily-imported component in a Suspense boundary with a shared
 * loading spinner. Keeps route definitions clean and ensures every lazy
 * component has a visible fallback.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyEl(factory: () => Promise<{ default: ComponentType<any> }>) {
  const Component = lazy(factory);
  return (
    <Suspense fallback={suspenseFallback}>
      <Component />
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Layout shells (lazy-loaded once)
// ---------------------------------------------------------------------------

const Logout = lazy(() => import('../Logout'));
const AppLayout = lazy(() => import('../Layout/AppLayout'));
const UserLayout = lazy(() => import('../Layout/UserLayout'));
const PublicLayout = lazy(() => import('../Layout/PublicLayout'));

// ---------------------------------------------------------------------------
// Public routes (no auth required, PublicLayout)
// ---------------------------------------------------------------------------

export const publicRoutes: AppRoute[] = [
  {
    path: '/invoice/:invoiceId',
    title: 'Public Invoice',
    breadcrumb: 'Public Invoice',
    element: lazyEl(() => import('../../scenes/Public/InvoicePublicPage')),
  },
  {
    path: '/public/invoice',
    element: <Navigate to="/login" replace />,
  },
  {
    path: '/capture/:token',
    title: 'Invoice Capture',
    breadcrumb: 'Invoice Capture',
    element: lazyEl(() => import('../../scenes/Public/CapturePage')),
  },
  {
    path: '/service-history/:vin',
    title: 'Public Service History',
    breadcrumb: 'Public Service History',
    element: lazyEl(() => import('../../scenes/Public/ServiceHistoryPublicPage')),
  },
];

// ---------------------------------------------------------------------------
// User routes (login / password flows, UserLayout)
// ---------------------------------------------------------------------------

export const userRoutes: AppRoute[] = [
  {
    path: '/login',
    title: 'LogIn',
    breadcrumb: 'LogIn',
    element: lazyEl(() => import('../../scenes/Login')),
  },
  {
    path: '/two-factor-verification',
    title: 'Two-Factor Verification',
    breadcrumb: 'Two-Factor Verification',
    element: lazyEl(() => import('../../scenes/TwoFactorVerification').then((m) => ({ default: m.TwoFactorVerification }))),
  },
  {
    path: '/account/reset-password',
    title: 'Reset Password',
    breadcrumb: 'Reset Password',
    element: lazyEl(() => import('../../scenes/Password/ResetPassword')),
  },
  {
    path: '/forgot-password',
    title: 'Forgot Password',
    breadcrumb: 'Forgot Password',
    element: lazyEl(() => import('../../scenes/Password/ForgotPassword')),
  },
  {
    path: '/not-found',
    element: lazyEl(() => import('../../scenes/Exception/NotFound')),
  },
  {
    path: '*',
    element: lazyEl(() => import('../../scenes/Login')),
  },
];

// ---------------------------------------------------------------------------
// App routes (authenticated, AppLayout)
// ---------------------------------------------------------------------------

export const appRoutes: AppRoute[] = [
  {
    path: '/dashboard',
    permission: 'Pages.Tenants',
    title: 'Reporting',
    breadcrumb: 'Reporting',
    icon: <HomeOutlined />,
    showInMenu: true,
    element: lazyEl(() => import('../../scenes/Dashboard')),
  },
  {
    path: '/calendar',
    permission: 'Pages.MyGarage',
    title: 'Calendar',
    breadcrumb: 'Calendar',
    icon: <CalendarOutlined />,
    showInMenu: true,
    element: lazyEl(() => import('../../scenes/Booking/Calendar')),
  },
  {
    path: '/garages',
    permission: 'Pages.Tenants',
    title: 'Garages',
    breadcrumb: 'Garages',
    icon: <ShopOutlined />,
    showInMenu: true,
    element: lazyEl(() => import('../../scenes/Tenants')),
  },
  {
    path: '/concerns',
    permission: 'Pages.Tenants',
    title: 'Concerns',
    breadcrumb: 'Concerns',
    icon: <AlertOutlined />,
    showInMenu: true,
    element: lazyEl(() => import('../../scenes/Concerns')),
  },
  {
    path: '/concerns/:id',
    permission: 'Pages.Tenants',
    title: 'Edit Concerns',
    breadcrumb: '/ Edit Concerns',
    icon: <AppstoreOutlined />,
    element: lazyEl(() => import('../../scenes/Concerns/ConcernCategoryEdit')),
  },
  {
    path: '/tenant-concerns/:id',
    permission: '',
    title: 'Edit Garage Concerns',
    breadcrumb: '/ Edit Garage Concerns',
    icon: <AppstoreOutlined />,
    element: lazyEl(() => import('../../scenes/Concerns/TenantConcernsEdit')),
  },
  {
    path: '/garages/:id',
    permission: 'Pages.Tenants',
    title: 'Edit Tenant',
    breadcrumb: '/ Edit Tenant',
    icon: <AppstoreOutlined />,
    element: lazyEl(() => import('../../scenes/Tenants/TenantEdit')),
  },
  {
    path: '/services',
    permission: 'Pages.Tenants',
    title: 'Services',
    breadcrumb: 'Services',
    icon: <ToolOutlined />,
    showInMenu: true,
    element: lazyEl(() => import('../../scenes/Services')),
  },
  {
    path: '/services/:id',
    permission: 'Pages.Tenants',
    title: 'Edit Service',
    breadcrumb: '/ Edit Service',
    icon: <AppstoreOutlined />,
    element: lazyEl(() => import('../../scenes/Services/Service/ServiceEdit')),
  },
  {
    path: '/service-groups/:id',
    permission: 'Pages.Tenants',
    title: 'Edit Service Group',
    breadcrumb: '/ Edit Service Group',
    icon: <AppstoreOutlined />,
    element: lazyEl(() => import('../../scenes/Services/ServiceGroupEdit')),
  },
  {
    path: '/orders',
    permission: 'Pages.MyGarage.Manager',
    title: 'Jobs',
    breadcrumb: 'Jobs',
    icon: <FormOutlined />,
    showInMenu: true,
    element: lazyEl(() => import('../../scenes/ServiceOrders')),
  },
  {
    path: '/my-customers',
    permission: 'Pages.MyGarage.Manager',
    title: 'Customers',
    breadcrumb: 'Customers',
    icon: <TeamOutlined />,
    showInMenu: true,
    element: lazyEl(() => import('../../scenes/MyCustomers')),
  },
  {
    path: '/my-customers/:id',
    permission: 'Pages.MyGarage.Manager',
    title: 'Edit Customer',
    breadcrumb: '/ Edit Customer',
    icon: <AppstoreOutlined />,
    element: lazyEl(() => import('../../scenes/MyCustomers/MyCustomerEdit')),
  },
  {
    path: '/my-customers-view/:id',
    permission: 'Pages.MyGarage.Manager',
    title: 'View Customer',
    breadcrumb: '/ View Customer',
    icon: <AppstoreOutlined />,
    element: lazyEl(() => import('../../scenes/MyCustomers/MyCustomerView')),
  },
  // {
  //   path: '/vehicle-histories',
  //   title: 'Vehicle Histories',
  //   breadcrumb: 'Vehicle Histories',
  //   icon: <HistoryOutlined />,
  //   showInMenu: true,
  //   element: lazyEl(() => import('../../scenes/VehicleHistories')),
  // },
  {
    path: '/invoices',
    permission: 'Pages.MyGarage.Manager',
    title: 'Invoices',
    breadcrumb: 'Invoices',
    icon: <FileTextOutlined />,
    showInMenu: true,
    element: lazyEl(() => import('../../scenes/Invoices')),
  },
  {
    path: '/invoices/:id',
    permission: 'Pages.MyGarage.Manager',
    title: 'Invoice Details',
    breadcrumb: '/ Invoice Details',
    icon: <AppstoreOutlined />,
    element: lazyEl(() => import('../../scenes/Invoices/InvoicePreview')),
  },
  {
    permission: 'Pages.MyGarage.Manager',
    title: 'Inventory',
    icon: <DatabaseOutlined />,
    showInMenu: true,
    children: [
      {
        path: '/stock',
        permission: 'Pages.MyGarage.Manager',
        title: 'Stock',
        breadcrumb: 'Stock',
        icon: <ContainerOutlined />,
        showInMenu: true,
        element: lazyEl(() => import('../../scenes/Inventory')),
      },
      {
        path: '/inventory-movements',
        permission: 'Pages.MyGarage.Manager',
        title: 'History',
        breadcrumb: 'History',
        icon: <HistoryOutlined />,
        showInMenu: true,
        element: lazyEl(() => import('../../scenes/InventoryMovements')),
      },
      {
        path: '/suppliers',
        permission: 'Pages.MyGarage.Manager',
        title: 'Suppliers',
        breadcrumb: 'Suppliers',
        icon: <TruckOutlined />,
        showInMenu: true,
        element: lazyEl(() => import('../../scenes/Suppliers')),
      },
    ],
  },
  {
    path: '/orders/:id',
    permission: 'Pages.MyGarage',
    title: 'Edit Service Order',
    breadcrumb: '/ Edit job card',
    icon: <AppstoreOutlined />,
    element: lazyEl(() => import('../../scenes/ServiceOrders/OrderEdit')),
  },
  {
    permission: 'Pages.MyGarage.Manager',
    title: 'Settings',
    icon: <SettingOutlined />,
    showInMenu: true,
    children: [
      {
        path: '/my-garage',
        permission: 'Pages.MyGarage.Manager',
        title: 'My Garage',
        breadcrumb: 'My Garage',
        icon: <ShopOutlined />,
        showInMenu: true,
        element: lazyEl(() => import('../../scenes/MyGarage')),
      },
      {
        path: '/my-services',
        permission: 'Pages.MyGarage.Manager',
        title: 'My Services',
        breadcrumb: 'My Services',
        icon: <ToolOutlined />,
        showInMenu: true,
        element: lazyEl(() => import('../../scenes/MyServices')),
      },
      {
        path: '/my-reports',
        permission: 'Pages.MyGarage.Manager',
        title: 'My Reports',
        breadcrumb: 'My Reports',
        icon: <BarChartOutlined />,
        showInMenu: true,
        element: lazyEl(() => import('../../scenes/MyReports/MetabaseDashboard')),
      },
      {
        path: '/tenant-concerns',
        permission: 'Pages.MyGarage.Manager',
        title: 'Online booking setup',
        breadcrumb: 'Online booking setup',
        icon: <AppstoreOutlined />,
        showInMenu: true,
        element: lazyEl(() => import('../../scenes/Concerns/TenantConcernTable')),
      },
    ],
  },
  {
    path: '/my-account',
    permission: '',
    title: 'My Account',
    breadcrumb: 'My Account',
    icon: <UserOutlined />,
    element: lazyEl(() => import('../../scenes/MyAccount')),
  },
  {
    path: '/my-account/:id',
    permission: '',
    title: 'Edit Account',
    breadcrumb: '/ Edit Account',
    icon: <AppstoreOutlined />,
    element: lazyEl(() => import('../../scenes/MyAccount/MyAccountDetailsEdit')),
  },
  {
    path: '/chat',
    permission: 'Pages.MyGarage.Manager',
    title: 'Chat',
    breadcrumb: 'Chat',
    icon: <CommentOutlined />,
    showInMenu: true,
    element: lazyEl(() => import('../../scenes/Chat')),
  },
  {
    path: '/change-password',
    permission: '',
    title: 'Change Password',
    breadcrumb: 'Change Password',
    icon: <AppstoreOutlined />,
    element: lazyEl(() => import('../../scenes/Password/ChangePassword')),
  },
  {
    path: '/logout',
    permission: '',
    title: 'Logout',
    element: (
      <Suspense fallback={suspenseFallback}>
        <Logout />
      </Suspense>
    ),
  },
];

// ---------------------------------------------------------------------------
// Router tree
// ---------------------------------------------------------------------------

export const routes: RouteObject[] = [
  {
    element: (
      <Suspense fallback={suspenseFallback}>
        <PublicLayout />
      </Suspense>
    ),
    children: [...publicRoutes],
  },
  {
    element: (
      <Suspense fallback={suspenseFallback}>
        <AppLayout />
      </Suspense>
    ),
    children: [{ element: <RequireAuth />, children: [...appRoutes] }],
  },
  {
    element: (
      <Suspense fallback={suspenseFallback}>
        <UserLayout />
      </Suspense>
    ),
    children: [...userRoutes],
  },
];

export const router = createBrowserRouter(routes);
